import type {
  ConsultantBulkCreateDeadlinesDto,
  ConsultantClaimDeadlinesDto,
  ConsultantUpdateDeadlineDto,
} from '@shared/dto/deadline/consultant-deadline-write.dto';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useTranslation } from 'react-i18next';
import type { RenewDeadlineVariables } from '../../pages/documents-archive/deadlines/use-deadline-mutations';
import { ConsultantDeadlineActionsService } from '../../services/consultant/consultant-deadline-actions.service';
import type { IConsultantDeadlineActionsService } from '../../services/consultant/consultant-deadline-actions.service.interface';
import { ASSISTANT_CARD_BUSY_CODE } from '../../utils/assistant-card-error-codes';
import {
  getDeadlineErrorCode,
  resolveDeadlineErrorMessage,
  resolveTakeOverErrorMessage,
} from '../../utils/deadline-error.utils';
import { getHttpErrorCode, getHttpErrorStatus } from '../../utils/error.utils';
import { openPresignedUrlInNewTab } from '../../utils/open-presigned-url-in-new-tab';
import { useAuthUser } from '../use-auth-user';
import { useNotification } from '../use-notification';
import { consultantDeadlineQueryKeys } from './use-consultant-deadlines';

export const consultantDeadlineClaimableKey = (consultantUserId: string) =>
  [...consultantDeadlineQueryKeys.all(consultantUserId), 'claimable'] as const;

function useActionsContext() {
  const service = useInjection<IConsultantDeadlineActionsService>(ConsultantDeadlineActionsService);
  const queryClient = useQueryClient();
  const consultantUserId = useAuthUser()?.sub ?? '';
  const { showSuccess, showError, showInfo } = useNotification();
  const { t } = useTranslation();

  const refreshDeadlines = () =>
    queryClient.invalidateQueries({ queryKey: consultantDeadlineQueryKeys.all(consultantUserId) });
  const notifyError = (error: unknown) => showError(resolveDeadlineErrorMessage(error, t));
  const notifyTakeOverError = (error: unknown) => showError(resolveTakeOverErrorMessage(error, t));

  return {
    service,
    refreshDeadlines,
    notifyError,
    notifyTakeOverError,
    showSuccess,
    showInfo,
    showError,
    t,
  };
}

export interface ConsultantTakeOverVariables {
  deadlineId: string;
  /** `YYYY-MM-DD` */
  until: string;
  note?: string;
}

/** Nessuna invalidazione dell'archivio documenti: il workspace consulente non legge quello del cliente. */
export function useTakeOverConsultantDeadline() {
  const { service, refreshDeadlines, notifyTakeOverError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (variables: ConsultantTakeOverVariables) =>
      service.takeOver(variables.deadlineId, { until: variables.until, note: variables.note }),
    onSuccess: () => {
      void refreshDeadlines();
      showSuccess(t('documents:deadlines.takeOver.success'));
    },
    onError: notifyTakeOverError,
  });
}

export function useCancelConsultantTakeOver() {
  const { service, refreshDeadlines, notifyTakeOverError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (deadlineId: string) => service.cancelTakeOver(deadlineId),
    onSuccess: () => {
      void refreshDeadlines();
      showSuccess(t('documents:deadlines.takeOver.cancelSuccess'));
    },
    onError: notifyTakeOverError,
  });
}

/** Sola lettura: nessuna invalidazione. L'errore usa il testo del download dell'archivio. */
export function useOpenConsultantDeadlineDocument() {
  const { service, showError, t } = useActionsContext();
  return useMutation({
    mutationFn: (deadlineId: string) =>
      openPresignedUrlInNewTab(() => service.getDocumentUrl(deadlineId)),
    onError: () => showError(t('documents:errorDownload')),
  });
}

export function useCreateConsultantDeadlines() {
  const { service, refreshDeadlines, notifyError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (dto: ConsultantBulkCreateDeadlinesDto) => service.createBulk(dto),
    onSuccess: (report) => {
      void refreshDeadlines();
      if (report.skipped.length === 0 && report.failed.length === 0) {
        showSuccess(
          t('consultant.deadlines.create.createdToast', { count: report.created.length }),
        );
      }
    },
    onError: (error) => {
      // The dialog opened from the card says it inline, next to the save it asks to repeat.
      if (getHttpErrorCode(error) !== ASSISTANT_CARD_BUSY_CODE) {
        notifyError(error);
      }
    },
  });
}

export function useUpdateConsultantDeadline() {
  const { service, refreshDeadlines, notifyError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (variables: { deadlineId: string; dto: ConsultantUpdateDeadlineDto }) =>
      service.update(variables.deadlineId, variables.dto),
    onSuccess: () => {
      void refreshDeadlines();
      showSuccess(t('consultant.deadlines.actions.updated'));
    },
    onError: notifyError,
  });
}

export function useArchiveConsultantDeadline() {
  const { service, refreshDeadlines, notifyError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (deadlineId: string) => service.archive(deadlineId),
    onSuccess: () => {
      void refreshDeadlines();
      showSuccess(t('consultant.deadlines.actions.archived'));
    },
    onError: notifyError,
  });
}

/** Un 400 senza motivo è la richiesta del server di motivare: la mostra il campo del dialog, non un toast. */
export function useDeleteConsultantDeadline() {
  const { service, refreshDeadlines, notifyError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (variables: { deadlineId: string; reason?: string }) =>
      service.remove(variables.deadlineId, variables.reason),
    onSuccess: () => {
      void refreshDeadlines();
      showSuccess(t('consultant.deadlines.actions.deleted'));
    },
    onError: (error, variables) => {
      const isReasonRequest =
        getHttpErrorStatus(error) === 400 &&
        getDeadlineErrorCode(error) === undefined &&
        !variables.reason;
      if (!isReasonRequest) {
        notifyError(error);
      }
    },
  });
}

export function useCloseConsultantDeadline() {
  const { service, refreshDeadlines, notifyError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (deadlineId: string) => service.archive(deadlineId, 'NO_LONGER_NEEDED'),
    onSuccess: () => {
      void refreshDeadlines();
      showSuccess(t('consultant.deadlines.actions.closed'));
    },
    onError: notifyError,
  });
}

export function useRestoreConsultantDeadline() {
  const { service, refreshDeadlines, notifyError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (deadlineId: string) => service.restore(deadlineId),
    onSuccess: () => {
      void refreshDeadlines();
      showSuccess(t('consultant.deadlines.actions.restored'));
    },
    onError: notifyError,
  });
}

export function useRenewConsultantDeadline() {
  const { service, refreshDeadlines, notifyError, showSuccess, t } = useActionsContext();
  return useMutation({
    mutationFn: (variables: RenewDeadlineVariables) =>
      service.renew(variables.deadlineId, {
        file: variables.file,
        uploadToken: variables.uploadToken,
        issuedAt: variables.issuedAt,
        dueAt: variables.dueAt,
        validityMonths: variables.validityMonths,
        assigneeUserId: variables.assigneeUserId,
        requestClientSignature: variables.requestClientSignature,
      }),
    onSuccess: (_result, variables) => {
      void refreshDeadlines();
      showSuccess(
        t(
          variables.attachOnly
            ? 'consultant.deadlines.actions.attached'
            : 'consultant.deadlines.actions.renewed',
        ),
      );
    },
    onError: notifyError,
  });
}

export function useClaimConsultantDeadlines() {
  const { service, refreshDeadlines, notifyError, showSuccess, showInfo, t } = useActionsContext();
  return useMutation({
    mutationFn: (dto: ConsultantClaimDeadlinesDto) => service.claim(dto),
    onSuccess: (report) => {
      void refreshDeadlines();
      showSuccess(t('consultant.deadlines.claim.claimedToast', { count: report.claimed.length }));
      if (report.skipped.length > 0) {
        const reasons = [...new Set(report.skipped.map((entry) => entry.reason))].map((reason) =>
          t(`consultant.deadlines.claim.skippedReason.${reason}`),
        );
        showInfo(
          `${t('consultant.deadlines.claim.skippedInfo', { count: report.skipped.length })} ${reasons.join(' ')}`,
        );
      }
    },
    onError: notifyError,
  });
}

export function useClaimableDeadlines() {
  const service = useInjection<IConsultantDeadlineActionsService>(ConsultantDeadlineActionsService);
  const consultantUserId = useAuthUser()?.sub ?? '';
  const { data, isLoading } = useQuery({
    queryKey: consultantDeadlineClaimableKey(consultantUserId),
    queryFn: () => service.getClaimable(),
    enabled: consultantUserId !== '',
  });
  return { claimable: data, isLoading };
}
